/**
 * Each Majik page's first-visit tour (Gideon, Oct 28): three to five steps,
 * one idea each — what the page is for, the thing you'll do most, where its
 * key control is. It plays once per person per page (the `toursSeen`
 * preference), and the page's "Take the tour" plays it again.
 *
 * Steps point at a screen's furniture, never at a record, so they hold with
 * real data: a `data-tour` name, `block:<heading>`, `button:<label>` or
 * `css:<selector>` (tour-target.ts). A step whose part may be missing — a
 * list that's empty, a panel that's closed — is `optional` and skipped.
 */

export interface PageTourStep {
  target: string
  caption: string
  optional?: boolean
}

export interface PageTour {
  /** Stored in the `toursSeen` preference: lowercase words joined by dashes. */
  id: string
  /** Shown on the card: "Jobs · Step 1 of 4". */
  title: string
  /** Which address it belongs to. */
  matches: (pathname: string, search: URLSearchParams) => boolean
  steps: PageTourStep[]
}

const record = (section: string) => new RegExp(`^/${section}/[^/]+$`)
const tab = (search: URLSearchParams) => search.get('tab') ?? ''

export const PAGE_TOURS: PageTour[] = [
  {
    id: 'today',
    title: 'Today',
    matches: (path) => path === '/',
    steps: [
      {
        target: 'block:Needs your attention',
        caption:
          'What’s waiting on someone, most urgent first — a visit with no cleaner, late money, invoices to send. Each row opens the thing to fix.',
      },
      {
        target: 'css:main [aria-label="The day in numbers"]',
        caption: 'Jobs today and this week, what’s owed, and what came in this month.',
      },
      {
        target: 'block:Today and tomorrow',
        caption:
          'The first visits today and tomorrow and who’s on them. See all opens the whole day on the Schedule.',
      },
      {
        target: 'css:header [aria-keyshortcuts]',
        caption:
          'Search finds any client, job, invoice or person by name or number. ⌘K works anywhere.',
      },
    ],
  },
  {
    id: 'jobs',
    title: 'Jobs',
    matches: (path, search) => path === '/jobs' && search.get('view') !== 'deleted',
    steps: [
      {
        target: 'jobs-cards',
        caption: 'Today, this week, ready to bill. Press a card to show just those jobs.',
      },
      {
        target: 'jobs-filters',
        caption:
          'Narrow the list by status, client, cleaner, work type, billing or date. Clear all brings everything back.',
      },
      {
        target: 'jobs-table',
        caption: 'One row per visit. Open a row to change its time, cleaner or price.',
      },
      {
        target: 'button:New job',
        caption:
          'New job makes a one-off visit. Regular visits come from a location’s recurring jobs.',
      },
    ],
  },
  {
    id: 'job',
    title: 'A job',
    matches: (path) => record('jobs').test(path),
    steps: [
      {
        target: 'job-header',
        caption: 'Its day, time and status. Edit changes them; ⋯ holds Message, Cancel and Delete.',
      },
      {
        target: 'block:Details',
        caption:
          'Where it is, the work, and how it repeats — the repeat line opens its recurring job.',
      },
      {
        target: 'block:Billing',
        caption:
          'The price, and where the money is: billed after the visit, on an invoice, or paid.',
      },
      {
        target: 'block:Activity',
        caption: 'Everything that happened to this job, every text included.',
      },
    ],
  },
  {
    id: 'clients',
    title: 'Clients',
    matches: (path, search) => path === '/clients' && search.get('view') !== 'deleted',
    steps: [
      {
        target: 'css:main [aria-label="Client totals"]',
        caption: 'How many clients, what they owe, and how much of it is late.',
      },
      {
        target: 'clients-filters',
        caption:
          'Filter by how they’re billed, whether they pay on time, their terms or their locations.',
      },
      {
        target: 'clients-table',
        caption:
          'One row per client; a late payer is tinted amber. Open one for its places, people and billing.',
      },
      {
        target: 'button:New client',
        caption:
          'New client starts with the name and how they’re billed; locations and people come next.',
      },
    ],
  },
  {
    id: 'client',
    title: 'A client',
    matches: (path, search) =>
      record('clients').test(path) && (tab(search) === '' || tab(search) === 'overview'),
    steps: [
      {
        target: 'client-header',
        caption:
          'The client and whether they’re active; Edit changes their name, terms and billing.',
      },
      {
        target: 'client-tabs',
        caption: 'Jobs and Invoices for the whole client; each location has a tab of its own.',
      },
      {
        target: 'client-locations',
        caption: 'Each place you clean. Open one for its weekly service, recurring jobs and notes.',
      },
      {
        target: 'client-people',
        caption: 'Who you deal with — the main contact, the others, and who gets the invoices.',
      },
    ],
  },
  {
    id: 'location',
    title: 'A location',
    matches: (path, search) => record('clients').test(path) && tab(search).startsWith('location-'),
    steps: [
      {
        target: 'location-header',
        caption: 'This place’s address and status; its ⋯ adds a recurring job or pauses them all.',
      },
      {
        target: 'block:Weekly service',
        caption: 'The regular week here, worked out from the recurring jobs below.',
      },
      {
        target: 'block:Recurring jobs here',
        caption: 'Each regular visit. Switch one off to pause it — it asks first.',
      },
      {
        target: 'block:Coming up',
        caption: 'The next visits. Dashed blue ones are planned and become jobs a few days ahead.',
      },
    ],
  },
  {
    id: 'cleaners',
    title: 'Cleaners',
    matches: (path) => path === '/cleaners',
    steps: [
      {
        target: 'css:main [aria-label="Cleaner totals"]',
        caption:
          'The team: who’s active, regular hours, fill-ins, and the languages texts go out in.',
      },
      {
        target: 'cleaners-table',
        caption:
          'One row per cleaner — shift, language, regular hours, where they work, their next job.',
      },
      {
        target: 'cleaners-broadcast',
        caption: 'Send Blast Message texts every cleaner, or just the daytime or nighttime team.',
      },
      {
        target: 'button:New cleaner',
        caption:
          'New cleaner adds someone to the team — their mobile, the language for texts, and their shift.',
      },
    ],
  },
  {
    id: 'cleaner',
    title: 'A cleaner',
    matches: (path, search) =>
      record('cleaners').test(path) && (tab(search) === '' || tab(search) === 'overview'),
    steps: [
      {
        target: 'cleaner-header',
        caption:
          'Their shift, language and whether job texts are on. Message opens your conversation.',
      },
      {
        target: 'cleaner-week',
        caption:
          'Their regular week, worked out from the recurring jobs they’re on. Its ⋯ downloads it as a PDF.',
      },
      {
        target: 'block:Time off',
        caption: 'Time off they’ve booked. While they’re away, their visits show Needs cover.',
      },
      {
        target: 'block:Details',
        caption: 'Phone, email, shift, and when their daily schedule is texted.',
      },
    ],
  },
  {
    id: 'invoices',
    title: 'Invoices',
    matches: (path, search) => path === '/invoices' && search.get('view') !== 'deleted',
    steps: [
      {
        target: 'css:main [aria-label="Invoice totals"]',
        caption:
          'What’s owed, what’s late, what’s waiting to be sent, and what came in this month.',
      },
      {
        target: 'css:main [data-group="action"]',
        caption:
          'Needs action is the to-do list: drafts to review, invoices to send, and late ones.',
      },
      {
        target: 'invoices-table',
        caption:
          'Each invoice with its next step — when it goes out, the next reminder, or how late it is.',
      },
      {
        target: 'button:New invoice',
        caption:
          'New invoice makes a draft from completed jobs, a month, or your own lines. Nothing leaves until you send it.',
      },
    ],
  },
  {
    id: 'invoice',
    title: 'An invoice',
    matches: (path) => record('invoices').test(path),
    steps: [
      {
        target: 'invoice-header',
        caption:
          'Its status and its one main action — Send now for a draft, Send reminder for a late one.',
      },
      {
        target: 'invoice-lines',
        caption: 'What it bills, line by line; a visit’s line opens its job.',
      },
      {
        target: 'block:Payment reminders',
        caption:
          'The reminders: which have gone, and when the next one is due. They stop once it’s paid.',
        optional: true,
      },
      {
        target: 'block:What happens next',
        caption:
          'What happens to a draft: its visits are added, then it’s sent, then reminders start.',
        optional: true,
      },
      {
        target: 'block:Activity',
        caption: 'Every email and reminder — sent, or recorded as would send while Go live is off.',
      },
    ],
  },
  {
    id: 'messages',
    title: 'Messages',
    matches: (path) => path === '/messages',
    steps: [
      // A phone shows the list or one conversation, so every step may be missing.
      {
        target: 'messages-inbox || css:main nav[aria-label="Conversations"]',
        caption: 'Every conversation, newest first. Filter to clients, cleaners or unread.',
        optional: true,
      },
      {
        target: 'messages-system',
        caption:
          'System: per location or cleaner, what went to no one — Monday’s automation errors, notes, and reminders with nobody to send to.',
        optional: true,
      },
      {
        target: 'css:main section[aria-label^="Conversation with"]',
        caption:
          'The conversation, newest at the bottom. Reply in the box under it — on a computer, Enter sends.',
        optional: true,
      },
      {
        target: 'button:Details',
        caption:
          'Details: who they are, what they owe, and their next job — with New job beside it.',
        optional: true,
      },
      {
        target: 'button:New message',
        caption:
          'New message writes to anyone with a mobile; Send Blast Message texts the cleaners.',
        optional: true,
      },
    ],
  },
  {
    id: 'schedule',
    title: 'Schedule',
    matches: (path) => path === '/schedule',
    steps: [
      {
        target: 'schedule-weeks',
        caption:
          'Every visit, a day to a column. Scroll sideways or use the arrows; Today brings you back.',
      },
      {
        target: 'css:main [data-day] [class*="border-dashed"]',
        caption:
          'Dashed blue visits are planned from a recurring job. Press one to change just it, or the series.',
        optional: true,
      },
      {
        target: 'button:New job',
        caption:
          'New job asks one-off or recurring. Or press a day’s empty space for a one-off that day.',
      },
    ],
  },
  {
    id: 'orders',
    title: 'Orders',
    matches: (path) => path === '/orders',
    steps: [
      {
        target: 'orders-list',
        caption: 'Supplies on their way to clients, newest first, and where each one is.',
      },
      {
        target:
          'css:main [data-tour="orders-list"] tbody tr || css:main [data-tour="orders-list"] li',
        caption:
          'Open an order to move it on — ready, out for delivery, delivered. Delivered supplies go on the next invoice.',
        optional: true,
      },
      {
        target: 'button:New order',
        caption: 'New order picks a location and its supplies; move it on until it’s delivered.',
      },
    ],
  },
  {
    id: 'inventory',
    title: 'Inventory',
    matches: (path) => path === '/inventory',
    steps: [
      {
        target: 'inventory-low-stock',
        caption: 'Supplies at or below their low mark — time to count or reorder.',
        optional: true,
      },
      {
        target:
          'css:main [data-slot="table-container"] || css:main ul[aria-label="Products and stock"]',
        caption:
          'Every product, its price to the client and its stock. Open one to change the price or count it.',
      },
      {
        target: 'button:New product',
        caption: 'New product adds a supply, a piece of equipment or a service you sell.',
      },
    ],
  },
  {
    id: 'inspections',
    title: 'Inspections',
    matches: (path) => path === '/inspections',
    steps: [
      {
        target: 'block:Inspectors',
        caption: 'The people who check your work. The page’s ⋯ adds one from your contacts.',
      },
      {
        target: 'block:Inspections sent',
        caption: 'Each inspection you’ve sent — who, which jobs, and your note.',
      },
      {
        target: 'button:New inspection',
        caption: 'New inspection: pick the jobs to check, add a note, and text the inspector.',
      },
    ],
  },
  {
    id: 'settings-business',
    title: 'Business',
    matches: (path) => path === '/settings/business',
    steps: [
      {
        target: 'business-go-live',
        caption:
          'Go live sends texts and emails for real. It stays off until everything on its checklist is done.',
      },
      {
        target: 'block:Business rules',
        caption:
          'The office numbers, when notices and schedules go out, sales tax and reminders — every screen reads these.',
      },
      {
        target: 'block:Teams and alerts',
        caption: 'Who hears about what: payments, late invoices, replies, gaps and low stock.',
      },
      {
        target: 'block:Connections',
        caption: 'QuickBooks, texting, email and translation — and whether each is connected.',
      },
    ],
  },
]

/** The tour for an address, if its page has one. */
export function tourFor(pathname: string, search: string): PageTour | undefined {
  const params = new URLSearchParams(search)
  return PAGE_TOURS.find((tour) => tour.matches(pathname, params))
}
